import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ActivityKind, NewActivity } from "@/api/queries";
import { ACTIVITY_KINDS, useCompanyContacts, useCreateActivity } from "@/api/queries";
import { useMe } from "@/auth/useMe";
import { Dialog } from "@/components/Dialog";
import { Button, FormField, Input, Select, Textarea } from "@/components/ui";
import { useAgents } from "@/leads/queries";
import type { PickedCompany } from "./CompanyPicker";
import { CompanyPicker } from "./CompanyPicker";

/**
 * Screen 06 of the Modernist design — *Agregar seguimiento* (.docs/08 §7 F2).
 *
 * The most-used modal in the CRM, and the reason it is built this early: Inicio,
 * Agenda, the company detail and the event detail all open it, so every later
 * phase gets it for free. Its only host today is the `+ Seguimiento` button F1
 * put on the unassigned group of screen 02.
 *
 * Three rules it does not own and must not restate:
 *
 * - **Completion.** There is no *Completado* box: the kind decides (D42). An
 *   *a futuro* kind stays pending, on the agenda; any other is logged as done.
 *   The hint under *Tipo* says which, and the server is what decides it.
 * - **Approval.** Scheduled work lands `Por autorizar`; the server decides that
 *   from `Activity.KINDS_REQUIRING_APPROVAL`. Sign-off is not in use yet, so
 *   the modal neither warns about it nor offers the status: the agenda and the
 *   landing screen show waiting work next to approved work.
 * - **Ownership.** `current_agent` is stamped server-side. The client never
 *   sends it — that is how the legacy app ended up with records owned by
 *   whoever last edited them.
 */

/** How the two halves of `due_at` start out: today, on the next round hour. */
function defaultWhen(): { date: string; time: string } {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 1);
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    date: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  };
}

type Draft = {
  kind: ActivityKind;
  company: string;
  contact: string;
  date: string;
  time: string;
  comment: string;
  assigned_to: string;
};

/**
 * The five kinds that are a promise rather than a record (4, 5, 6, 14 and 15).
 * Only for the hint and for the next-step list: whether a row is done is the
 * server's to decide, from `Activity.SCHEDULED_KINDS` (D42).
 */
const FUTURE_KINDS: readonly ActivityKind[] = [5, 4, 6, 14, 15];

function emptyDraft(
  company?: number,
  contact?: number | null,
  futureOnly = false,
): Draft {
  const { date, time } = defaultWhen();
  return {
    // Llamada de seguimiento: the commonest entry by a wide margin, and the
    // design's own default. *Llamada a futuro* when only the future will do.
    kind: futureOnly ? 5 : 1,
    company: company ? String(company) : "",
    contact: contact ? String(contact) : "",
    date,
    time,
    comment: "",
    assigned_to: "",
  };
}

export function NuevoSeguimiento({
  open,
  onClose,
  company,
  companyName,
  contact,
  completes,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** Pre-selected and fixed. Omit to let the seller pick, or log with none. */
  company?: number;
  /** Only used to name the fixed company; the picker fetches its own. */
  companyName?: string;
  /** Pre-selected, still changeable. */
  contact?: number | null;
  /**
   * The follow-up this one comes after. Saving marks that one completed, in the
   * same request, and this one is held to what makes it a next step: only the
   * five *a futuro* kinds, a date that has not passed, and nothing logged as
   * already done.
   */
  completes?: number;
  /** Called once a follow-up is written — unlike `onClose`, not on Cancelar. */
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const futureOnly = completes !== undefined;
  const [draft, setDraft] = useState<Draft>(() =>
    emptyDraft(company, contact, futureOnly),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const create = useCreateActivity(completes);

  const isManager = me?.is_manager ?? false;
  // `/api/users/agents/` is the one endpoint where a signed-in user sees other
  // people, and it 403s for anyone who cannot hand work over. Asking for it
  // from a plain seller's modal would be a failed request on every open.
  const canAssign = isManager || (me?.can_triage ?? false);
  const agents = useAgents(canAssign);

  const [pickedCompany, setPickedCompany] = useState<PickedCompany | null>(null);
  const fixedCompany = company !== undefined;

  const companyId = Number(draft.company);
  const hasCompany = Number.isInteger(companyId) && companyId > 0;
  const contacts = useCompanyContacts(companyId, hasCompany);
  // A contact management switched off is not someone to log new work against.
  const contactOptions = (contacts.data?.results ?? []).filter(
    (option) => !option.disabled_at,
  );

  useEffect(() => {
    if (open) {
      setDraft(emptyDraft(company, contact, futureOnly));
      setPickedCompany(null);
      setError(null);
      setSaved(false);
    }
  }, [open, company]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  async function save(another: boolean) {
    if (!draft.comment.trim()) {
      setError(t("newFollowUp.commentRequired"));
      return;
    }
    if (!draft.date || !draft.time) {
      setError(t("newFollowUp.dateRequired"));
      return;
    }
    // Local time in, UTC out. The input pair is what the seller reads on a
    // clock in Monterrey; `due_at` is a moment, and the server stores moments.
    const due = new Date(`${draft.date}T${draft.time}`);
    if (Number.isNaN(due.getTime())) {
      setError(t("newFollowUp.dateRequired"));
      return;
    }
    if (futureOnly && due.getTime() <= Date.now()) {
      setError(t("newFollowUp.dateMustBeFuture"));
      return;
    }
    setError(null);

    const body: NewActivity = {
      kind: draft.kind,
      due_at: due.toISOString(),
      comment: draft.comment.trim(),
      company: hasCompany ? companyId : null,
      contact: draft.contact ? Number(draft.contact) : null,
      assigned_to: draft.assigned_to ? Number(draft.assigned_to) : null,
    };

    try {
      await create.mutateAsync(body);
      onSaved?.();
      if (!another) {
        onClose();
        return;
      }
      // "Guardar y agregar otro" keeps the company and the kind — the reason
      // to click it is a run of follow-ups on the same account — and clears
      // what is specific to the one just written.
      setDraft((previous) => ({
        ...previous,
        contact: "",
        comment: "",
      }));
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.createActivity"));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // What is typed here is the record: a click beside it does not close it.
      closeOnBackdrop={false}
      kicker={t("newFollowUp.kicker")}
      title={t("newFollowUp.title")}
      width={820}
      footer={
        <>
          <Button
            variant="primary"
            disabled={create.isPending}
            onClick={() => void save(false)}
          >
            {create.isPending ? t("newFollowUp.saving") : t("newFollowUp.save")}
          </Button>
          {futureOnly ? null : (
            <Button disabled={create.isPending} onClick={() => void save(true)}>
              {t("newFollowUp.saveAndAnother")}
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}
      {saved && !error ? (
        <p role="status" className="mb-4 border-2 border-rule px-3 py-2 text-sm">
          {t("newFollowUp.saved")}
        </p>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-6">
        <div className="sm:col-span-2">
          <FormField
            label={t("newFollowUp.kind")}
            hint={t(
              FUTURE_KINDS.includes(draft.kind)
                ? "newFollowUp.pendingHint"
                : "newFollowUp.completedHint",
            )}
          >
            {(id) => (
              <Select
                id={id}
                value={String(draft.kind)}
                data-autofocus=""
                onChange={(event) =>
                  set("kind", Number(event.target.value) as ActivityKind)
                }
              >
                {(futureOnly ? FUTURE_KINDS : ACTIVITY_KINDS).map((kind) => (
                  <option key={kind} value={String(kind)}>
                    {t(`activityKind.${kind}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </div>

        {fixedCompany ? (
          <FormField
            label={t("newFollowUp.company")}
            hint={t("newFollowUp.companyFixed")}
          >
            {(id) => <Input id={id} value={companyName ?? `#${company}`} readOnly />}
          </FormField>
        ) : (
          <CompanyPicker
            // A fresh picker each time the modal opens, so the last search is
            // not still sitting in the box.
            key={String(open)}
            value={pickedCompany}
            canWorkOnOthers={isManager}
            onChange={(picked) => {
              setPickedCompany(picked);
              // The contact belonged to the previous company, and the server
              // refuses a pairing that does not match.
              setDraft((previous) => ({
                ...previous,
                company: picked ? String(picked.id) : "",
                contact: "",
              }));
            }}
          />
        )}

        <FormField
          label={t("newFollowUp.contact")}
          hint={
            hasCompany && contacts.data !== undefined && contactOptions.length === 0
              ? t("newFollowUp.contactUnavailable")
              : undefined
          }
        >
          {(id) => (
            <Select
              id={id}
              value={draft.contact}
              disabled={!hasCompany}
              onChange={(event) => set("contact", event.target.value)}
            >
              <option value="">{t("newFollowUp.noContact")}</option>
              {contactOptions.map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.job_title
                    ? `${option.name} — ${option.job_title}`
                    : option.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormField label={t("newFollowUp.date")}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={draft.date}
              onChange={(event) => set("date", event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newFollowUp.time")}>
          {(id) => (
            <Input
              id={id}
              type="time"
              value={draft.time}
              onChange={(event) => set("time", event.target.value)}
            />
          )}
        </FormField>

        <div className="sm:col-span-2">
          <FormField label={t("newFollowUp.comment")}>
            {(id) => (
              <Textarea
                id={id}
                rows={4}
                value={draft.comment}
                placeholder={t("newFollowUp.commentPlaceholder")}
                className="resize-y font-sans"
                onChange={(event) => set("comment", event.target.value)}
              />
            )}
          </FormField>
        </div>

        {canAssign ? (
          <FormField label={t("newFollowUp.assignedTo")}>
            {(id) => (
              <Select
                id={id}
                value={draft.assigned_to}
                onChange={(event) => set("assigned_to", event.target.value)}
              >
                <option value="">{t("newFollowUp.assignedToMe")}</option>
                {(agents.data ?? []).map((option) => (
                  <option key={option.id} value={String(option.id)}>
                    {option.name || `#${option.id}`}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ) : null}
      </div>
    </Dialog>
  );
}
